import React from "react";
import styled from "styled-components";
import { media } from "styled-bootstrap-grid";
import { getImage } from "gatsby-plugin-image";
import { Container, Col, Row } from "styled-bootstrap-grid";
import { StaticImage, GatsbyImage } from "gatsby-plugin-image";
import { graphql, useStaticQuery, Link } from "gatsby";
import NavLink from "../../common/Link/NavLink";
import ContactButton from "../../common/Button/ContactButton";
import HamburgerButton from "../../common/Button/HamburgerButton";
import { Dealer, DatoDealer } from "../../../types";

type DearlerDetailProps = {
  datoDealer: DatoDealer;
  dealer: Dealer;
};

type ContentContainerProps = {
  imageWidth: number;
  imageHeight: number;
};

const HeroSection = styled.section`
  position: relative;
`;

const HeroImageContainer = styled.div`
  display: none;
  ${media.lg`
    display: block;
    height: 700px;
    overflow: hidden;
    background-color: #000005;
  `};
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`;

const MobileImageContainer = styled.div`
  display: block;
  height: 700px;
  overflow: hidden;
  background-color: #000005;
  ${media.lg`
    display: none;
  `};
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`;

const HeroContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 30px;
  position: absolute;
  bottom: 0;
  top: 150px;
  left: 0;
  right: 0;
`;

const ForegroundContainer = styled.div<ContentContainerProps>`
  width: 100%;
  height: 100%;
  &::before {
    content: "";
    width: 1px;
    margin-left: -1px;
    float: left;
    height: 0;
  }
  &::after {
    /* to clear float */
    content: "";
    display: table;
    clear: both;
  }
`;

const HeroForeground = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 0;
`;

const HeroContent = styled(Container)`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: #ffffff;
  ${media.lg`
    margin-top: 0;
    justify-content: center;
  `}
  .gatsby-image-wrapper {
    width: 100%;
    height: auto;
  }
`;

const Heading = styled.h1`
  color: #ffffff;
  line-height: 1em;
  font-size: 36px;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0;
  font-family: "BTBrik-XXLOblique" !important;
  margin: 20px 0;
  ${media.lg`
    font-size: 72px;
  `}
`;

const SubHeading = styled.h2`
  color: #ffffff;
  font-size: 22px;
  letter-spacing: 0;
  line-height: 28px;
  margin: 10px 0;
  font-weight: 700;
  font-family: "DIN2014";
  text-transform: uppercase;

  ${media.lg`
    font-size: 28px;
    margin: 5px 0;
  `}
`;

const NavigationCol = styled(Col)`
  text-align: right;
`;

const Navigation = styled.ul`
  list-style: none;
  text-align: right;
  display: inline-flex;
  flex-direction: row;
`;

const NavigationItem = styled.li`
  margin-right: 30px;
  text-align: center;

  &:last-child {
    margin-right: 0;
  }
`;

const Header = styled.header`
  background-color: #000005;
  width: 100%;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  background-color: #000005;
  padding: 20px 10px;

  ${media.lg`
    padding: 20px 50px;
  `}
`;

const LogoContainer = styled.div`
  margin: 40px 0;
`;

const DealerHero: React.FC<DearlerDetailProps> = ({ dealer, datoDealer }) => {
  const data = useStaticQuery(graphql`
    query {
      desktopHeroImage: file(
        relativePath: { eq: "OTTICO_RGB_Pattern_Medium_Mono_Black.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, height: 566, width: 1455)
        }
      }
      mobileHeroImage: file(
        relativePath: { eq: "OTTICO_RGB_Pattern_Small_Mono_Black.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, aspectRatio: 1)
        }
      }
    }
  `);
  const defaultDesktopImage = getImage(data.desktopHeroImage);
  const defaultMobileImage = getImage(data.mobileHeroImage);

  const dealerName = dealer.name.includes("-")
    ? dealer.name.split("-")[0]
    : dealer.name;

  return (
    <HeroSection>
      <Header>
        <Container fluid>
          <Row alignItems="center">
            <Col col={6} hiddenLgUp>
              <Link to="/" aria-label="Home Page">
                <StaticImage
                  src="../../../images/OTTICO_RGB_Logo_Product_SPITWATER_Negative.png"
                  alt="logo"
                  width={160}
                  placeholder="blurred"
                  layout="fixed"
                />
              </Link>
            </Col>
            <Col col={3} hiddenLgDown>
              <Link to="/" aria-label="Home Page">
                <StaticImage
                  src="../../../images/OTTICO_RGB_Logo_Product_SPITWATER_Negative.png"
                  alt="logo"
                  width={252}
                  placeholder="blurred"
                  layout="fixed"
                />
              </Link>
            </Col>
            <NavigationCol lg={9} hiddenLgDown>
              <Navigation>
                <NavigationItem>
                  <NavLink to="/find-a-solution/">Find a solution</NavLink>
                </NavigationItem>
                <NavigationItem>
                  <NavLink to="/products/">Products</NavLink>
                </NavigationItem>
                <NavigationItem>
                  <NavLink to="/academy/">Academy</NavLink>
                </NavigationItem>
                <NavigationItem>
                  <NavLink to="/about-us/">About us</NavLink>
                </NavigationItem>
                <NavigationItem>
                  <NavLink to="/careers/">Careers</NavLink>
                </NavigationItem>
                <NavigationItem>
                  <ContactButton to="/contact-us/">Contact</ContactButton>
                </NavigationItem>
              </Navigation>
            </NavigationCol>
            <NavigationCol col={3} offset={3} hiddenLgUp>
              <HamburgerButton />
            </NavigationCol>
          </Row>
        </Container>
      </Header>
      <MobileImageContainer>
        <ForegroundContainer
          imageWidth={
            datoDealer?.mobileImage
              ? datoDealer.mobileImage.gatsbyImageData.width
              : (defaultMobileImage ? defaultMobileImage.width: 200)
          }
          imageHeight={
            datoDealer?.mobileImage
              ? datoDealer.mobileImage.gatsbyImageData.height
              : (defaultMobileImage ? defaultMobileImage.height: 200)
          }
        >
          <GatsbyImage
            image={
              datoDealer?.mobileImage?.gatsbyImageData || defaultMobileImage
            }
            alt="hero-dealer"
            objectFit="cover"
          />
          <HeroContentContainer>
            <HeroForeground>
              <HeroContent>
                <Row>
                  <Col col={12} lg={6}>
                    {datoDealer?.logo ? (
                      <GatsbyImage
                        image={datoDealer.logo.gatsbyImageData}
                        alt="product"
                      />
                    ) : (
                      <StaticImage
                        src="../../../images/roylances-logo.png"
                        alt="logo"
                        width={318}
                        placeholder="blurred"
                        layout="fixed"
                      />
                    )}
                    <Heading>Sitwater {dealer.city}</Heading>
                    <SubHeading>Meet the team at</SubHeading>
                    <SubHeading>{dealerName}</SubHeading>
                    <LogoContainer>
                      <StaticImage
                        src="../../../images/made-in-aus-1982-grey.svg"
                        alt="logo"
                        width={250}
                        placeholder="blurred"
                        layout="fixed"
                      />
                    </LogoContainer>
                    <StaticImage
                      src="../../../images/OTTICO_RGB_Promise_Inline_Negative.png"
                      alt="logo"
                      width={250}
                      placeholder="blurred"
                      layout="fixed"
                    />
                  </Col>
                </Row>
              </HeroContent>
            </HeroForeground>
          </HeroContentContainer>
        </ForegroundContainer>
      </MobileImageContainer>
      <HeroImageContainer>
        <ForegroundContainer
          imageWidth={
            datoDealer?.heroImage
              ? datoDealer.heroImage.gatsbyImageData.width
              : (defaultDesktopImage ? defaultDesktopImage.width: 200)
          }
          imageHeight={
            datoDealer?.heroImage
              ? datoDealer.heroImage.gatsbyImageData.height
              :(defaultDesktopImage ? defaultDesktopImage.height: 200)
          }
        >
          <GatsbyImage
            image={datoDealer?.heroImage.gatsbyImageData || defaultDesktopImage}
            alt="hero-dealer"
            objectFit="cover"
          />
          <HeroContentContainer>
            <HeroForeground>
              <HeroContent>
                <Row>
                  <Col col={12} lg={6}>
                    <StaticImage
                      src="../../../images/roylances-logo.png"
                      alt="logo"
                      width={318}
                      placeholder="blurred"
                      layout="fixed"
                    />
                    <Heading>Sitwater {dealer.city}</Heading>
                    <SubHeading>Meet the team at</SubHeading>
                    <SubHeading>{dealerName}</SubHeading>
                    <LogoContainer>
                      <StaticImage
                        src="../../../images/made-in-aus-1982-grey.svg"
                        alt="logo"
                        width={250}
                        placeholder="blurred"
                        layout="fixed"
                      />
                    </LogoContainer>
                    <StaticImage
                      src="../../../images/OTTICO_RGB_Promise_Inline_Negative.png"
                      alt="logo"
                      width={420}
                      placeholder="blurred"
                      layout="fixed"
                    />
                  </Col>
                </Row>
              </HeroContent>
            </HeroForeground>
          </HeroContentContainer>
        </ForegroundContainer>
      </HeroImageContainer>
    </HeroSection>
  );
};

export default DealerHero;
